import { useTranslation } from 'react-i18next';
import { AiMessageFeedbackEnum } from 'graphql-common';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import styles from './AiAssistant.module.scss';

type Props = {
  messageId: string;
  // The message's current persisted rating (null = not yet rated).
  feedback: AiMessageFeedbackEnum | null;
  disabled?: boolean;
  // Apply / clear a like immediately (no reason).
  onLike: (messageId: string) => void;
  // Open the dislike modal (reason + optional comment) for this message.
  onDislike: (messageId: string) => void;
  // Remove the current rating.
  onRemove: (messageId: string) => void;
};

function MessageFeedback(props: Props) {
  const { messageId, feedback, disabled, onLike, onDislike, onRemove } = props;
  const { t } = useTranslation();
  const isLiked = feedback === AiMessageFeedbackEnum.Positive;
  const isDisliked = feedback === AiMessageFeedbackEnum.Negative;

  // Clicking the active thumb clears the rating; otherwise like applies
  // instantly and dislike opens the reason modal.
  const handleLike = () => (isLiked ? onRemove(messageId) : onLike(messageId));
  const handleDislike = () =>
    isDisliked ? onRemove(messageId) : onDislike(messageId);

  // Once a rating exists, only the active thumb stays — clicking it clears the
  // rating and brings both buttons back.
  return (
    <div className={styles.feedbackRow}>
      {!isDisliked && (
        <IconButton
          icon="thumb_up"
          aria-label={t('ai-feedback-like')}
          symbolsOutlined
          iconSize={16}
          type={
            isLiked
              ? IconButtonTypes.QuaternaryFilled
              : IconButtonTypes.TertiaryFilled
          }
          size={IconButtonSizes.Small}
          onClick={handleLike}
          disabled={disabled}
          rounded
          filled={isLiked}
        />
      )}
      {!isLiked && (
        <IconButton
          icon="thumb_down"
          aria-label={t('ai-feedback-dislike')}
          symbolsOutlined
          iconSize={16}
          type={
            isDisliked
              ? IconButtonTypes.QuaternaryFilled
              : IconButtonTypes.TertiaryFilled
          }
          size={IconButtonSizes.Small}
          onClick={handleDislike}
          disabled={disabled}
          rounded
          filled={isDisliked}
        />
      )}
    </div>
  );
}

export default MessageFeedback;
